// Kasa Özeti ("Kasa Defteri Raporu Detaylı") hesaplaması — /api/daily-report yanıtından.
// Web'deki Kasa Özeti yazdırma çıktısı (RetailSales printKasaOzeti) ve iOS admin uygulamasının
// WebView'da açtığı /mobile/kasa-ozeti sayfası (MobileKasaOzeti) bu fonksiyonu kullanır;
// rakamlar tek yerde hesaplanır, iOS ile web arasında kayma olmaz.

export const stripBankAccountType = (label) => (label || '').replace(/\s*\((Şirket|Şahsi|Şahıs|E-Ticaret)\)\s*$/i, '');

// ---- E-Ticaret: Sanal Pos (eski site) + Shopify siparişleri ----
// Backend Shopify satışlarını report.sanalPos (E-Ticaret) toplamına katar; shopifyTotal ve
// shopifyByGateway ile ayrıca verir. Raporlarda Sanal Pos kısmı eskisi gibi "E-Ticaret", Shopify kısmı
// "E-Ticaret (Shopify iyzico)" satırı olur. Toplamlar (gelir, kasa genel) değişmez.
export const isShopifySale = (s) => typeof s?.payment_method === 'string' && s.payment_method.startsWith('shopify_');
export const isETicaretSale = (s) => s?.payment_method === 'Sanal Pos' || isShopifySale(s);
// backend/server.js computeReportFromData shopifyPaymentLabel ile aynı
export const shopifyPaymentLabel = (pm) => {
  const g = String(pm || '').slice('shopify_'.length);
  const gl = g.toLocaleLowerCase('tr');
  if (gl.includes('iyzico')) return 'Shopify iyzico';
  if (/manual|havale|eft|bank/.test(gl)) return 'Shopify Havale/EFT';
  return `Shopify ${g.split(' - ')[0] || 'Diğer'}`.trim();
};
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// [{ key: 'sanal'|'Shopify iyzico'…, amount }] — Sanal Pos kısmı ve Shopify ödeme yöntemleri
export const splitETicaret = (sp) => {
  const total = Number(sp?.total) || 0;
  const shopTotal = Number(sp?.shopifyTotal) || 0;
  const parts = [{ key: 'sanal', amount: round2(total - shopTotal) }];
  const gw = sp?.shopifyByGateway && Object.keys(sp.shopifyByGateway).length ? sp.shopifyByGateway : (shopTotal ? { 'Shopify iyzico': { total: shopTotal } } : {});
  Object.entries(gw).forEach(([k, v]) => parts.push({ key: k, amount: round2(v.total) }));
  return parts;
};
// İstemci tarafında satış listesinden E-Ticaret özeti (backend computeReportFromData ile aynı alanlar)
export const eTicaretSummary = (sales, calcTotal) => {
  const list = (sales || []).filter(s => isETicaretSale(s) && s.sale_type !== 'refund');
  const shop = list.filter(isShopifySale);
  const shopifyByGateway = {};
  shop.forEach(s => {
    const k = shopifyPaymentLabel(s.payment_method);
    if (!shopifyByGateway[k]) shopifyByGateway[k] = { count: 0, total: 0 };
    shopifyByGateway[k].count += 1;
    shopifyByGateway[k].total += calcTotal([s]);
  });
  return { count: list.length, total: calcTotal(list), shopifyCount: shop.length, shopifyTotal: calcTotal(shop), shopifyByGateway };
};
// E-Ticaret kartı/satırı başlığı: Shopify satışı varsa "E-Ticaret", yoksa eskisi gibi "Sanal Pos"
export const eTicaretCardLabel = (sp) => ((Number(sp?.shopifyCount) || 0) > 0 ? 'E-Ticaret' : 'Sanal Pos');

// Gelir satırları: [etiket, tutar] (tutar > 0)
export const eTicaretGelirRows = (sp) => splitETicaret(sp).filter(p => p.amount > 0)
  .map(p => [p.key === 'sanal' ? 'E-Ticaret' : `E-Ticaret (${p.key})`, p.amount]);
// Alt özet satırları: [etiket, tutar] (tutar ≠ 0)
export const eTicaretNetRows = (sp) => splitETicaret(sp).filter(p => p.amount !== 0)
  .map(p => [p.key === 'sanal' ? 'NET E-TİCARET (Sanal Pos)' : `NET E-TİCARET (${p.key})`, p.amount]);

export const formatKasaAmount = (v) => (v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Özet satırı değeri: işaretli satırlarda tutarın sonuna (+)/(-) eklenir
export const formatKasaSummaryValue = ({ amount, signed }) =>
  formatKasaAmount(amount) + (signed ? ` ${(amount || 0) >= 0 ? '(+)' : '(-)'}` : '');

// Havale/EFT gelirini hesap bazlı satırlara böler; hesap kırılımı toplamı karşılamıyorsa
// kalan tutar "Diğer" satırına düşer, kırılım yoksa tek satır gösterilir.
const pushTransferRows = (items, total, byAccount, prefix, plainLabel, otherLabel) => {
  const accounts = byAccount || {};
  const names = Object.keys(accounts);
  if (total > 0 && names.length > 0) {
    names.filter(n => accounts[n].total > 0).forEach(name => {
      items.push({ label: `${prefix} ${stripBankAccountType(name).replace('HAVALE ', '')}`, amount: accounts[name].total });
    });
    const accountsTotal = names.reduce((s, n) => s + (accounts[n].total || 0), 0);
    if (total > accountsTotal + 0.01) items.push({ label: otherLabel, amount: total - accountsTotal });
  } else if (total > 0) {
    items.push({ label: plainLabel, amount: total });
  }
};

export const computeKasaOzeti = (r) => {
  const cash = r.cash || {};
  const card = r.card || {};
  const transfer = r.transfer || {};

  // (+) GELİRLER
  const gelirItems = [];
  const pushGelir = (label, amount) => { if (amount > 0) gelirItems.push({ label, amount }); };
  pushGelir('Nakit Ödeme', cash.salesTotal ?? ((cash.total || 0) - (cash.collectionTotal || 0) - (cash.repairTotal || 0)));
  pushGelir('Nakit Tahsilat', cash.collectionTotal || 0);
  pushGelir('Nakit Tamirat', cash.repairTotal || 0);
  eTicaretGelirRows(r.sanalPos).forEach(([label, amount]) => pushGelir(label, amount));
  pushGelir('Kredi Kartı', card.salesTotal ?? card.total);
  pushGelir('Kredi Kartı Tahsilat', card.collectionTotal || 0);
  pushGelir('Kredi Kartı Tamirat', card.repairTotal || 0);
  pushTransferRows(gelirItems, transfer.salesTotal ?? transfer.total ?? 0, transfer.byAccount, 'Havale/EFT', 'Havale/EFT', 'Havale/EFT Diğer');
  pushTransferRows(gelirItems, transfer.collectionTotal || 0, transfer.collectionByAccount, 'Tahsilat', 'Havale/EFT Tahsilat', 'Havale/EFT Tahsilat Diğer');
  pushTransferRows(gelirItems, transfer.repairTotal || 0, transfer.repairByAccount, 'Havale/EFT Tamirat', 'Havale/EFT Tamirat', 'Havale/EFT Tamirat Diğer');
  pushGelir('Bakiye', r.voucher?.total);
  pushGelir('Altın Hurda', r.scrap?.saleLinkedGoldTotal);
  pushGelir('Gümüş Hurda', r.scrap?.saleLinkedSilverTotal);
  pushGelir('Hurda ile Tahsilat', r.collections?.hurdaTotal || 0);
  const gelirTop = (cash.total || 0) + (r.sanalPos?.total || 0) + (card.salesTotal ?? card.total ?? 0) + (card.collectionTotal || 0) + (card.repairTotal || 0) + (transfer.salesTotal ?? transfer.total ?? 0) + (transfer.collectionTotal || 0) + (transfer.repairTotal || 0) + (r.voucher?.total || 0) + (r.scrap?.saleLinkedTotal || 0) + (r.collections?.hurdaTotal || 0);

  // (-) GİDERLER
  const giderSources = [
    ['Kredi Kartı İade', r.cardRefund?.total],
    ['Nakit İade', r.cashRefund?.total],
    ['Hurda Nakit İadesi', r.scrapCashRefund?.total],
    ['Sanal Pos İade', r.sanalPosRefund?.total],
    ['Havale İade', r.havaleRefund?.total],
    ['Değişim Nakit İade', r.exchanges?.cashRefundTotal],
    ['Değişim Kredi Kartı İade', r.exchanges?.cardRefundTotal],
    ['Değişim Havale İade', r.exchanges?.havaleRefundTotal],
    ['Masraf Nakit Ödeme', r.expenses?.total],
    ['Hurda Alımları', r.scrap?.standaloneTotal],
  ];
  const giderItems = giderSources.filter(([, amount]) => amount > 0).map(([label, amount]) => ({ label, amount }));
  const giderTop = giderSources.reduce((sum, [, amount]) => sum + (amount || 0), 0);

  // Alt özet: net kredi kartı ve hesap tipine göre (Şahıs / Şirket / E-Ticaret) net havale
  const netKrediKarti = ((card.salesTotal ?? 0) + (card.collectionTotal ?? 0) + (card.repairTotal ?? 0)) - ((r.cardRefund?.total ?? 0) + (r.exchanges?.cardRefundTotal ?? 0) + (r.scrap?.cardTotal ?? 0));
  const tba = transfer.byAccount || {};
  const cba = transfer.collectionByAccount || {};
  const rba = transfer.repairByAccount || {};
  const sba = r.scrap?.transferByAccount || {};
  const allAccounts = [...new Set([...Object.keys(tba), ...Object.keys(cba), ...Object.keys(rba), ...Object.keys(sba)])];
  let netHavaleSirket = 0, netHavaleSahis = 0, netHavaleETicaret = 0;
  allAccounts.forEach(a => {
    const net = ((tba[a]?.total ?? 0) + (cba[a]?.total ?? 0) + (rba[a]?.total ?? 0)) - (sba[a]?.total ?? 0);
    const al = a.toLocaleLowerCase('tr');
    if (al.includes('şahıs') || al.includes('sahis') || a.includes('(Şahıs)')) netHavaleSahis += net;
    else if (al.includes('e-ticaret') || al.includes('eticaret') || al.includes('e ticaret') || a.includes('E-TİCARET')) netHavaleETicaret += net;
    else netHavaleSirket += net;
  });

  const summaryRows = [];
  const pushSummary = (label, amount, signed) => { if ((amount || 0) !== 0) summaryRows.push({ label, amount, signed }); };
  pushSummary('NAKİT KASA TOPLAM', r.expectedCash, true);
  pushSummary('NET KREDİ KARTI', netKrediKarti, true);
  pushSummary('NET HAVALE ŞAHIS', netHavaleSahis, true);
  pushSummary('NET HAVALE ŞİRKET', netHavaleSirket, true);
  pushSummary('NET HAVALE E-TİCARET', netHavaleETicaret, true);
  eTicaretNetRows(r.sanalPos).forEach(([label, amount]) => pushSummary(label, amount, true));
  pushSummary('TOPLAM HURDA TUTARI', r.scrap?.total, false);
  pushSummary('AÇIK HESAP SATIŞLAR', r.openAccount?.total, false);
  pushSummary('MÜŞTERİ BAKİYESİ SATIŞLAR', r.musteriBakiyesiSales?.total, false);
  pushSummary('AÇIK HESABA İADELER', (r.exchanges?.voucherRefundTotal || 0) + (r.acikHesapSalesRefund?.total || 0) + (r.bakiyeRefund?.total || 0), false);
  pushSummary('İNDİRİMLER TOPLAM', r.totalDiscount, false);

  return { gelirItems, giderItems, gelirTop, giderTop, kasaGenel: gelirTop - giderTop, summaryRows };
};
